import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Check, Copy, ExternalLink, Heart, ImagePlus, KeyRound, Loader2, UploadCloud, X } from "lucide-react";
import api, { mediaSrc, uploadFileChunked } from "@/lib/api";
import { SectionTitle, formatApiError, ghostBtn, solidBtn } from "./shared";

const LibraryPicker = ({ exclude, onClose, onAdd }) => {
  const [library, setLibrary] = useState(null);
  const [picked, setPicked] = useState(new Set());

  useEffect(() => {
    api.get("/media", { params: { kind: "photo" } }).then((r) => setLibrary(r.data.filter((m) => !exclude.has(m.id)))).catch(() => setLibrary([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (id) =>
    setPicked((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  return (
    <div data-testid="admin-room-library-modal" className="fixed inset-0 z-[90] bg-ink/90 backdrop-blur-md flex items-center justify-center p-4 sm:p-10" onClick={onClose}>
      <div className="w-full max-w-4xl max-h-full flex flex-col border border-ivory/10 bg-coal" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-line">
          <SectionTitle icon={ImagePlus}>Add from Library</SectionTitle>
          <button data-testid="admin-room-library-close" aria-label="Close" onClick={onClose} className="text-ash hover:text-amber"><X size={16} /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1">
          {library === null ? (
            <Loader2 size={18} className="animate-spin text-amber mx-auto" />
          ) : library.length === 0 ? (
            <p className="font-mono text-[11px] text-mute">Every library photo is already in this room.</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {library.map((m, i) => {
                const on = picked.has(m.id);
                return (
                  <button key={m.id} data-testid={`admin-room-library-item-${i}`} aria-pressed={on} onClick={() => toggle(m.id)} className={`relative aspect-square overflow-hidden border transition-all ${on ? "border-amber" : "border-line hover:border-ivory/40"}`}>
                    <img src={mediaSrc(m)} alt={m.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                    {on && <span className="absolute top-1.5 right-1.5 w-6 h-6 bg-amber text-ink flex items-center justify-center"><Check size={12} /></span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="p-5 border-t border-line flex justify-end">
          <button data-testid="admin-room-library-add-button" disabled={picked.size === 0} onClick={() => onAdd([...picked])} className={`${solidBtn} px-8 py-3`}>
            Add {picked.size || ""} Frame{picked.size === 1 ? "" : "s"}
          </button>
        </div>
      </div>
    </div>
  );
};

export const RoomDetail = ({ roomId, onBack }) => {
  const [room, setRoom] = useState(null);
  const [picker, setPicker] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [copied, setCopied] = useState(false);
  const inputRef = useRef(null);

  const load = () => api.get(`/admin/rooms/${roomId}`).then((r) => setRoom(r.data)).catch(() => toast.error("Could not load room."));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  const link = `${window.location.origin}/proof/${room?.code}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
    } catch {}
    setCopied(true);
    toast.success("Private link copied.");
    setTimeout(() => setCopied(false), 1800);
  };

  const addFromLibrary = async (ids) => {
    try {
      const { data } = await api.post(`/admin/rooms/${roomId}/photos`, { media_ids: ids });
      toast.success(`${data.added} frame${data.added === 1 ? "" : "s"} added to the room.`);
      setPicker(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (files.length === 0) return;
    setUploading({ done: 0, total: files.length });
    let fail = 0;
    for (const f of files) {
      try {
        await uploadFileChunked(f, {
          title: f.name.replace(/\.[^.]+$/, ""),
          category: "Proofing",
          room_id: roomId,
        });
      } catch {
        fail += 1;
      }
      setUploading((u) => ({ ...u, done: u.done + 1 }));
    }
    setUploading(null);
    if (fail) toast.error(`${fail} upload${fail > 1 ? "s" : ""} failed.`);
    else toast.success(`${files.length} frame${files.length > 1 ? "s" : ""} uploaded to the room.`);
    load();
  };

  const removePhoto = async (m) => {
    try {
      await api.delete(`/admin/rooms/${roomId}/photos/${m.id}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (!room) {
    return <div className="flex justify-center py-20"><Loader2 size={20} className="animate-spin text-amber" /></div>;
  }

  const favs = new Set(room.favorites);

  return (
    <div data-testid="admin-room-detail" className="space-y-6">
      <section className="border border-line bg-coal p-6 sm:p-8">
        <button data-testid="admin-room-back-button" onClick={onBack} className="font-mono text-[10px] tracking-[0.25em] uppercase text-mute hover:text-amber flex items-center gap-2 transition-colors">
          <ArrowLeft size={12} /> All rooms
        </button>
        <div className="flex flex-wrap items-end justify-between gap-6 mt-5">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl text-ivory tracking-wide flex items-center gap-3">
              {room.client_name}
              {room.pin && (
                <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-amber border border-amber/40 px-2 py-1 flex items-center gap-1.5">
                  <KeyRound size={10} /> PIN {room.pin}
                </span>
              )}
            </h2>
            <p className="font-mono text-[10px] text-mute uppercase tracking-[0.2em] mt-2">{room.event || "Shoot"}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <code data-testid="admin-room-link" className="font-mono text-[11px] text-cream bg-ink border border-line px-4 py-2.5 select-all">{link}</code>
            <button data-testid="admin-room-copy-link-button" onClick={copyLink} className={ghostBtn}>
              {copied ? <Check size={12} className="text-amber" /> : <Copy size={12} />} {copied ? "Copied" : "Copy"}
            </button>
            <a data-testid="admin-room-preview-link" href={link} target="_blank" rel="noreferrer" className={ghostBtn}>
              <ExternalLink size={12} /> Preview
            </a>
          </div>
        </div>
      </section>

      <section className="border border-line bg-coal p-6 sm:p-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <SectionTitle count={room.photos.length}>Frames in this room</SectionTitle>
            <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-mute mt-2 flex items-center gap-2">
              <Heart size={11} className="text-amber fill-amber" />
              <span data-testid="admin-room-favorites-count">{room.favorites.length}</span> favourited by client · room-only uploads stay off the public site
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="admin-room-add-library-button" onClick={() => setPicker(true)} className={ghostBtn}>
              <ImagePlus size={12} /> From Library
            </button>
            <button data-testid="admin-room-upload-button" onClick={() => inputRef.current?.click()} disabled={!!uploading} className={ghostBtn}>
              {uploading ? <Loader2 size={12} className="animate-spin" /> : <UploadCloud size={12} />}
              {uploading ? `Uploading ${uploading.done}/${uploading.total}` : "Upload Photos"}
            </button>
            <input ref={inputRef} data-testid="admin-room-upload-input" type="file" accept="image/*" multiple className="hidden" onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} />
          </div>
        </div>

        {room.photos.length === 0 ? (
          <p className="font-mono text-[11px] text-mute mt-6 border border-dashed border-line p-10 text-center">
            Empty room. Pull frames from your library or upload the client's selects directly.
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mt-6" data-testid="admin-room-photo-grid">
            {room.photos.map((m, i) => (
              <div key={m.id} data-testid={`admin-room-photo-${i}`} className={`group relative aspect-square overflow-hidden border ${favs.has(m.id) ? "border-amber/60" : "border-line"} bg-ink`}>
                <img src={mediaSrc(m)} alt={m.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                {favs.has(m.id) && (
                  <span data-testid={`admin-room-photo-fav-${i}`} className="absolute top-2 left-2 w-7 h-7 bg-ink/80 flex items-center justify-center text-amber">
                    <Heart size={12} className="fill-amber" />
                  </span>
                )}
                <button data-testid={`admin-room-photo-remove-${i}`} aria-label={`Remove ${m.title}`} onClick={() => removePhoto(m)} className="absolute top-2 right-2 w-7 h-7 bg-ink/80 border border-line text-ash hover:text-red-400 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                  <X size={12} />
                </button>
                <p className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/90 to-transparent p-2 font-mono text-[9px] text-cream truncate">{m.title}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      {picker && <LibraryPicker exclude={new Set(room.photos.map((p) => p.id))} onClose={() => setPicker(false)} onAdd={addFromLibrary} />}
    </div>
  );
};
